/* eslint-disable @typescript-eslint/camelcase */
import { get } from 'lodash'
import { normalizeTag } from '~/util/cleanUpTag'
import {
  removeEmptyProperties,
  removeBRPlaceholders,
} from '~/modules/services/story/cleaning.js'
import { Circulation } from '~/types'

function setUrlRedirect(unattachedData, circulations: Circulation[]) {
  let redirectPropertyValue = [] as Array<object>

  if (
    unattachedData?.additional_properties?.is_published &&
    (unattachedData.redirect || false).forwardUrl
  ) {
    const primaryWebsiteCirculation = circulations.find(
      (circulation) =>
        circulation.website_id === unattachedData.canonical_website
    )
    const canonicalUrl = primaryWebsiteCirculation?.website_url ?? ''

    //  take the form data and convert it into an {} for related_content.redirect[0]
    //  following - https://github.com/washingtonpost/ans-schema/blob/master/src/main/resources/schema/ans/0.5.8/redirect.json
    redirectPropertyValue = [
      {
        type: 'redirect',
        version: unattachedData.version,
        canonical_url: canonicalUrl,
        redirect_url: unattachedData.redirect.forwardUrl,
      },
    ]
  }

  unattachedData.related_content = Object.assign(
    {},
    unattachedData.related_content,
    {
      redirect: redirectPropertyValue,
    }
  )

  delete unattachedData.redirect
}

// This is logic that mirrors (in part) the cleanup logic in the Angular story service, which does not run on React publish/update
export default function storyCleanup(storyData, circulations: Circulation[]) {
  let clonedStory = JSON.stringify(storyData)
  clonedStory = clonedStory
    .replace(/"\$\$hashKey":"[^"]*",/g, '')
    .replace(/,\s?"\$\$hashKey":"[^"]*"/g, '')
  /* eslint no-control-regex: "off" */
  if (clonedStory.match(/[\u0002\u0001]/g)) {
    clonedStory = clonedStory.replace(/[\u0002\u0001]/g, '')
  }

  const unattachedData = JSON.parse(clonedStory)

  // (Clean up before save) remove temp ID for story tags
  if (get(unattachedData, 'taxonomy.tags', []).length > 0) {
    unattachedData.taxonomy.tags = unattachedData.taxonomy.tags.map((tag) =>
      normalizeTag(tag)
    )
  }

  // fix until we figure out something else
  if (unattachedData.taxonomy && unattachedData.taxonomy.sections) {
    unattachedData.taxonomy.sections.forEach((o) => {
      if (o.referent) {
        delete o._id
        delete o.primary
      }
    })
  }

  unattachedData.credits &&
    unattachedData.credits.by &&
    unattachedData.credits.by.forEach((a) => {
      if (a._id && a._id.indexOf('_guest') === 0) {
        delete a._id
      }
    })

  removeEmptyProperties(unattachedData)
  removeBRPlaceholders(unattachedData)

  if (unattachedData.promo_items) {
    Object.keys(unattachedData.promo_items).forEach((k) => {
      const promoItem = unattachedData.promo_items[k]
      const ownKeysCount = Object.keys(promoItem).length
      const hasOnlyTypeKey = ownKeysCount === 1 && !!promoItem.type
      // Remove promo item if it has only an empty object or object
      // with only one key: 'type'
      // Empty objects with keys `basic` & `bright` are added during story load
      if (ownKeysCount === 0 || hasOnlyTypeKey) {
        delete unattachedData.promo_items[k]
      }
    })
  }

  // remove carriage returns from headline (EL-2680)
  if (unattachedData?.headlines?.basic) {
    unattachedData.headlines.basic = unattachedData.headlines.basic.replace(
      /(\r\n|\n|\r)/gm,
      ' '
    )
  }

  // Somehow TGAM is getting undefined saved into this property
  if (
    unattachedData.content_restrictions &&
    unattachedData.content_restrictions.content_code === undefined
  ) {
    try {
      delete unattachedData.content_restrictions.content_code
    } catch (e) {}
  }

  /**
   * ANS Schema: https://github.com/washingtonpost/ans-schema/tree/master/src/main/resources/schema/ans/0.10.10
   * remove `content` from where it's an invalid property
   */
  if (
    unattachedData.content_elements &&
    Array.isArray(unattachedData.content_elements)
  ) {
    unattachedData.content_elements.forEach((ele, indx) => {
      if (
        [
          'custom_embed',
          'divider',
          'content_zone',
          'correction',
          'endorsement',
          'link_list',
          'list',
          'numeric_rating',
          'quote',
          'table',
        ].includes(ele.type)
      ) {
        delete unattachedData.content_elements[indx].content
      } else if (ele.content !== undefined) {
        unattachedData.content_elements[indx].content = ele.content
      }
    })
  }

  if (unattachedData.planning && unattachedData.planning.story_length) {
    if (unattachedData.planning.story_length.word_count_planned === null) {
      delete unattachedData.planning.story_length.word_count_planned
    }
    if (unattachedData.planning.story_length.inch_count_planned === null) {
      delete unattachedData.planning.story_length.inch_count_planned
    }
    if (!Object.keys(unattachedData.planning.story_length).length) {
      delete unattachedData.planning.story_length
    }
  }

  setUrlRedirect(unattachedData, circulations)

  delete unattachedData.circulations
  // BostonGlobe templates have `isDirty` as ans field comp-6516
  delete unattachedData.isDirty

  return unattachedData
}
